/**
 * LosiInferenceVoicePicker
 *
 * Brain + voice selectors labeled as Losi Inference (no LiveKit branding).
 */

'use client'

import { useEffect, useState } from 'react'

import {
  DEFAULT_LOSI_INFERENCE_LLM,
  DEFAULT_LOSI_INFERENCE_VOICE,
  type LosiInferenceBrain,
  type LosiInferenceSessionOptions,
  type LosiInferenceVoice
} from '@/lib/losi-inference'
import { cn } from '@/lib/utils'

type CatalogResponse = {
  label: string
  configured: boolean
  brains: LosiInferenceBrain[]
  voices: LosiInferenceVoice[]
  defaults: LosiInferenceSessionOptions
}

interface LosiInferenceVoicePickerProps {
  value: LosiInferenceSessionOptions
  onChange: (next: LosiInferenceSessionOptions) => void
  disabled?: boolean
  className?: string
}

export function LosiInferenceVoicePicker({
  value,
  onChange,
  disabled,
  className
}: LosiInferenceVoicePickerProps) {
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/api/losi-inference/catalog')
      .then(res => res.json())
      .then((data: CatalogResponse) => {
        if (!cancelled) setCatalog(data)
      })
      .catch(() => {
        if (!cancelled) setCatalog(null)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const brains = catalog?.brains || []
  const voices = catalog?.voices || []

  return (
    <div className={cn('flex flex-col gap-2 text-xs', className)}>
      <div className="text-muted-foreground font-medium tracking-wide uppercase">
        {catalog?.label || 'Losi Inference'}
        {catalog && !catalog.configured ? (
          <span className="text-amber-600 dark:text-amber-400 ml-2 normal-case">
            (not configured)
          </span>
        ) : null}
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-muted-foreground">Brain</span>
        <select
          className="border-border bg-background rounded-md border px-2 py-1.5"
          disabled={disabled || brains.length === 0}
          value={value.llm || DEFAULT_LOSI_INFERENCE_LLM}
          onChange={e => onChange({ ...value, llm: e.target.value })}
        >
          {brains.map(brain => (
            <option key={brain.id} value={brain.id}>
              {brain.label}
              {brain.isVoiceDefault ? ' (default)' : ''}
              {brain.isBest ? ' (best)' : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-muted-foreground">Voice</span>
        <select
          className="border-border bg-background rounded-md border px-2 py-1.5"
          disabled={disabled || voices.length === 0}
          value={value.voice || DEFAULT_LOSI_INFERENCE_VOICE}
          onChange={e => onChange({ ...value, voice: e.target.value })}
        >
          {voices.map(voice => (
            <option key={voice.id} value={voice.id}>
              {voice.label} — {voice.description}
            </option>
          ))}
        </select>
      </label>
    </div>
  )
}
